import React, { useState, useEffect } from 'react';
import { Users, UserCheck, FileText, ClipboardList, Stethoscope, Pill } from 'lucide-react';
import apiService from '../services/api';
import ApiTest from '../components/ApiTest';

interface DashboardStats {
  totalUsers: number;
  totalDoctors: number;
  totalForms: number;
  totalFormResponses: number;
  totalInvestigations: number;
  totalPrescriptions: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalDoctors: 0,
    totalForms: 0,
    totalFormResponses: 0,
    totalInvestigations: 0,
    totalPrescriptions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [
          users,
          doctors,
          forms,
          formResponses,
          investigations,
          prescriptions
        ] = await Promise.all([
          apiService.getAllUsers(),
          apiService.getAllDoctors(),
          apiService.getAllForms(),
          apiService.getAllFormResponses(),
          apiService.getAllInvestigations(),
          apiService.getAllPrescriptions(),
        ]);

        setStats({
          totalUsers: users.length,
          totalDoctors: doctors.length,
          totalForms: forms.length,
          totalFormResponses: formResponses.length,
          totalInvestigations: investigations.length,
          totalPrescriptions: prescriptions.length,
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statsCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: <Users size={24} />,
      color: 'bg-blue-500',
      change: '+12%',
      changeType: 'positive' as const,
    },
    {
      title: 'Total Doctors',
      value: stats.totalDoctors,
      icon: <UserCheck size={24} />,
      color: 'bg-green-500',
      change: '+8%',
      changeType: 'positive' as const,
    },
    {
      title: 'Total Forms',
      value: stats.totalForms,
      icon: <FileText size={24} />,
      color: 'bg-purple-500',
      change: '+15%',
      changeType: 'positive' as const,
    },
    {
      title: 'Form Responses',
      value: stats.totalFormResponses,
      icon: <ClipboardList size={24} />,
      color: 'bg-orange-500',
      change: '+23%',
      changeType: 'positive' as const,
    },
    {
      title: 'Investigations',
      value: stats.totalInvestigations,
      icon: <Stethoscope size={24} />,
      color: 'bg-red-500',
      change: '+5%',
      changeType: 'positive' as const,
    },
    {
      title: 'Prescriptions',
      value: stats.totalPrescriptions,
      icon: <Pill size={24} />,
      color: 'bg-indigo-500',
      change: '+18%',
      changeType: 'positive' as const,
    },
  ];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Dashboard Overview</h2>
        <p className="text-gray-600">Welcome to the Swasth Bharat Admin Portal</p>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">Quick Actions</h2>
      {/* Quick Actions (Top, Inline, Small, Icon Only) */}
      <div className="flex gap-2 mb-6">
        <button className="btn btn-primary btn-sm" title="Add New Doctor">
          <UserCheck size={18} />
        </button>
        <button className="btn btn-primary btn-sm" title="Create Form">
          <FileText size={18} />
        </button>
        <button className="btn btn-primary btn-sm" title="New Prescription">
          <Pill size={18} />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        {statsCards.map((card, index) => (
          <div key={index} className="stats-card">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${card.color} text-white`}>
                {card.icon}
              </div>
              <div className="text-right">
                <div className="stats-number">{card.value}</div>
                <div className="stats-label">{card.title}</div>
                <div className={`stats-change ${card.changeType}`}>
                  {card.change} from last month
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <br /> <br /> 

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Users</h3>
          </div>
          <div className="card-content">
            <p className="text-gray-600">Latest user registrations and activities</p>
            {/* Add recent users list here */}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Form Responses</h3>
          </div>
          <div className="card-content">
            <p className="text-gray-600">Latest form submissions and responses</p>
            {/* Add recent form responses list here */}
          </div>
        </div>
      </div>

      {/* API Test Section */}
      <div className="mt-8">
        <ApiTest />
      </div>
    </div>
  );
};

export default Dashboard; 